import React, { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Box,
  Typography,
  Chip,
  Tooltip,
  Button,
  Paper,
  Stack,
} from "@mui/material";
import HTML from "bundles/common/components/HTML";
import { useSelector } from "react-redux";
import {
  Coffee,
  CircleDot,
  GraduationCap,
  Globe,
  HeartPulse,
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
  Mountain,
  Palette,
  Sparkles,
  Users,
  Calendar,
  TrendingUp,
  Briefcase,
  Award,
  MessageSquare,
  Building2,
  ChevronDown,
  Wifi,
} from "lucide-react";
import CompanyCard from "../../../Candidate/components/CompanyCard";
import SmallJobOfferCard from "../SmallJobOfferCard";

const SectionHeader = ({ icon: Icon, title }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
    {Icon && (
      <Box
        sx={{
          width: "2.25rem",
          height: "2.25rem",
          borderRadius: "0.5rem",
          border: "1px solid hsl(214.3, 31.8%, 91.4%)",
          bgcolor: "hsla(152, 60%, 32%, 0.12)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon size={17} color="hsl(152, 60%, 32%)" />
      </Box>
    )}
    <Typography
      sx={{
        fontSize: "1.25rem",
        color: "hsl(222.2, 84%, 4.9%)",
        letterSpacing: "-0.01em",
        fontFamily: "Segoe UI Bold, system-ui, sans-serif",
        borderBottom: Icon ? "none" : "2px solid hsl(152, 60%, 32%)",
      }}
    >
      {title}
    </Typography>
  </Box>
);

const toSentenceCase = (value) => {
  if (!value) return "";

  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
};

const ADVANTAGE_ICONS = {
  company_car: Briefcase,
  meal_vouchers: Coffee,
  fuel_card: TrendingUp,
  group_insurance: HeartPulse,
  mobility_plan: Mountain,
  phone: MessageSquare,
  trainings: GraduationCap,
  hospital_insurance: HeartPulse,
  company_restaurant: Coffee,
  open_space: Building2,
  parking: Building2,
  daycare: Users,
  team_building: Users,
  kitchen: Palette,
  homeworking: Wifi,
  remote_working: Wifi,
  on_site: Building2,
  flexible_holiday: Calendar,
  end_of_year_bonus: Award,
  starting_bonus: Award,
  thirteenth_month: Award,
  pension_plan: TrendingUp,
  bonus: Award,
};

const extractOfferAdvantages = (offerAdvantages = {}) => {
  const environments = offerAdvantages.environments || [];

  return [
    ...(offerAdvantages.advantages || []),
    ...environments,
    ...(offerAdvantages.schedules || []),
    ...(offerAdvantages.primes || []),
  ].filter(Boolean);
};

const formatAdvantage = (value) => {
  if (!value) return "";

  if (value.includes("_")) {
    return value
      .split("_")
      .map((segment) => toSentenceCase(segment))
      .join(" ");
  }

  return toSentenceCase(value);
};

const SOCIAL_ICON_MAP = {
  linkedin: Linkedin,
  instagram: Instagram,
  facebook: Facebook,
  youtube: Youtube,
  website: Globe,
};

const CompanyProfileTab = ({ company, onChange }) => {
  const intl = useIntl();
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const [showAllAdvantages, setShowAllAdvantages] = useState(false);
  const [showFullDesc, setShowFullDesc] = useState(false);
  const [sidebarMode, setSidebarMode] = useState("static");
  const [sidebarLeft, setSidebarLeft] = useState(0);
  const [sidebarWidth, setSidebarWidth] = useState(300);
  const [sidebarColumnHeight, setSidebarColumnHeight] = useState("auto");

  const layoutRef = useRef(null);
  const contentColumnRef = useRef(null);
  const sidebarColumnRef = useRef(null);
  const sidebarContentRef = useRef(null);

  const jobOffers = company?.job_offers || [];
  const companyImages = useMemo(
    () => (company?.company_images_records || []).map((image) => image?.url).filter(Boolean),
    [company?.company_images_records]
  );
  const aboutImage = companyImages[0];
  const galleryImages = companyImages.slice(1);

  const hasValidTeamSize = Boolean(company?.team_size && company.team_size !== "null");

  const stats = [
    {
      label: intl.formatMessage({ id: "company_view.profile.stats.team_size" }),
      value: hasValidTeamSize
        ? intl.formatMessage({ id: `company.company_size.options.${company.team_size}` })
        : null,
      icon: Users,
    },
    {
      label: intl.formatMessage({ id: "company_view.profile.stats.year_founded" }),
      value: company?.creation_year,
      icon: Calendar,
    },
    {
      label: intl.formatMessage({ id: "company_view.profile.stats.employee_average_age" }),
      value: company?.average_age,
      icon: TrendingUp,
    },
  ].filter((entry) => entry.value);

  const advantages = useMemo(() => {
    const jobOfferBenefits = (company?.job_offers || [])
      .flatMap((jobOffer) => extractOfferAdvantages(jobOffer?.offer_advantages || {}));

    return [...new Set([...jobOfferBenefits])];
  }, [company?.job_offers]);

  const socialLinks = useMemo(() => {
    const raw = company?.social_links || [];
    const entries = (Array.isArray(raw) ? raw : Object.entries(raw).map(([key, url]) => ({ name: key, url })))
      .filter((link) => Boolean(link?.url))
      .map((link) => ({
        key: link.name,
        url: link.url.startsWith("http") ? link.url : `https://${link.url}`,
        Icon: SOCIAL_ICON_MAP[link.name] || Globe,
      }));

    const domainUrl = company?.domain_name
      ? (company.domain_name.startsWith("http") ? company.domain_name : `https://${company.domain_name}`)
      : null;

    if (domainUrl && !entries.some((entry) => entry.url === domainUrl)) {
      entries.unshift({
        key: "domain_name",
        url: domainUrl,
        Icon: Globe,
      });
    }

    return entries;
  }, [company?.social_links, company?.domain_name]);

  const featuredTestimonial = useMemo(() => {
    const testimonials = company?.company_testimonials || [];
    return testimonials.find((testimonial) => testimonial?.featured) || testimonials[0] || null;
  }, [company?.company_testimonials]);

  const recruitmentSteps = useMemo(
    () =>
      (company?.recruitment_process || [])
        .filter((step) => step?.title && step?.description)
        .sort((a, b) => (a?.position || 0) - (b?.position || 0))
        .map((step) => ({ step: step.title, description: step.description })),
    [company?.recruitment_process]
  );

  const subsidiaries = company?.subsidiaries || [];
  const relatedCompanies = company?.related_companies || [];

  // Creates scroll listener to toggle sidebar between static, fixed and absolte position based on scroll position.
  // A bug keeps happening when we try to set position = sticky in CSS. 
  useEffect(() => {
    let rafId = null;

    const updateSidebarState = () => {
      if (!layoutRef.current || !contentColumnRef.current || !sidebarColumnRef.current || !sidebarContentRef.current) return;

      const isDesktop = window.innerWidth >= 900;
      if (!isDesktop) {
        setSidebarMode("static");
        setSidebarColumnHeight("auto");
        return;
      }

      const topOffset = 80;
      const layoutRect = layoutRef.current.getBoundingClientRect();
      const columnRect = sidebarColumnRef.current.getBoundingClientRect();
      const contentRect = sidebarContentRef.current.getBoundingClientRect();
      const pageTop = window.scrollY || window.pageYOffset;

      const layoutTop = layoutRect.top + pageTop;
      const contentHeight = contentColumnRef.current.offsetHeight;
      const sidebarHeight = sidebarContentRef.current.offsetHeight;
      const sidebarColumnTargetHeight = Math.max(contentHeight, sidebarHeight);

      setSidebarLeft(columnRect.left);
      setSidebarWidth(columnRect.width);
      setSidebarColumnHeight(sidebarColumnTargetHeight);

      const stickyStart = layoutTop - topOffset;
      const stickyEnd = layoutTop + contentHeight - sidebarHeight - topOffset;

      if (sidebarHeight >= contentHeight || pageTop < stickyStart) {
        setSidebarMode("static");
        return;
      }

      if (pageTop >= stickyEnd) {
        setSidebarMode("bottom");
        return;
      }

      if (contentRect.top <= topOffset + 1 || pageTop >= stickyStart) {
        setSidebarMode("fixed");
      }
    };

    const onScrollOrResize = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateSidebarState);
    };

    updateSidebarState();
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [company?.id, jobOffers.length, socialLinks.length]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <Box
        ref={layoutRef}
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "flex-start",
          gap: "2.5rem",
        }}
      >
        <Stack ref={contentColumnRef} spacing={5} sx={{ flex: 1, minWidth: 0 }}>
          <Box>
            {company?.about || company?.references || hasValidTeamSize || company?.creation_year || company?.average_age ? (
              <SectionHeader
                title={intl.formatMessage(
                  { id: "company_view.profile.about_title" },
                  {
                    companyName:
                      company?.name || intl.formatMessage({ id: "company_view.profile.this_company" }),
                  }
                )}
              />
            ) : null}

            {stats.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", mb: 2 }}>
                {stats.map((item) => {
                  const Icon = item.icon;

                  return (
                    <Tooltip key={item.label} title={item.label} arrow>
                      <Chip
                        icon={<Icon size={14} color="hsl(152, 60%, 32%)" />}
                        label={item.value}
                        sx={{
                          borderRadius: "999px",
                          bgcolor: "hsl(210, 40%, 96.1%)",
                          width: "auto",
                          fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                          maxWidth: "max-content",
                          "& .MuiChip-label": {
                            px: "0.75rem",
                            py: "0.375rem",
                            fontSize: "0.75rem",
                            lineHeight: 1,
                            color: "hsl(222.2, 84%, 4.9%)",
                          },
                          "& .MuiChip-icon": {
                            ml: "0.75rem",
                            mr: "-0.375rem",
                          },
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Box>
            )}

            {aboutImage && (
              <Box
                sx={{
                  float: { md: "right" },
                  ml: { md: "1.5rem" },
                  mb: "1rem",
                  width: { xs: "100%", md: "14rem" },
                  flexShrink: 0,
                }}
              >
                <Box
                  component="img"
                  src={aboutImage}
                  alt={`Inside ${company?.name || "company"} studio`}
                  sx={{
                    width: "100%",
                    height: { xs: "12rem", md: "23.4375rem" },
                    objectFit: "cover",
                    borderRadius: "0.75rem",
                  }}
                />
              </Box>
            )}

            {(() => {
              const combinedText = [company?.about, company?.references].filter(Boolean).join(" ");
              const isTruncatable = combinedText.length > 950;
              return (
                <>
                  <Box sx={{ color: "hsl(215.4, 16.3%, 46.9%)", fontSize: "0.9375rem", lineHeight: 1.85 }}>
                    {showFullDesc || !isTruncatable ? (
                      <>
                        {company?.about && <HTML html={company.about} />}
                        {company?.references && (
                          <Box sx={{ mt: 2 }}>
                            <Typography
                              sx={{
                                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                                fontSize: "0.9375rem",
                                color: "hsl(215.4, 16.3%, 46.9%)",
                                mb: 0.5,
                              }}
                            >
                              <FormattedMessage id="company.references" />
                            </Typography>
                            <HTML html={company.references} />
                          </Box>
                        )}
                      </>
                    ) : (
                      <HTML html={`${combinedText.slice(0, 950)}...`} />
                    )}
                  </Box>
                  {isTruncatable && (
                    <Button
                      onClick={() => setShowFullDesc((prev) => !prev)}
                      endIcon={<ChevronDown size={16} />}
                      disableRipple
                      sx={{
                        mt: "12px",
                        p: 0,
                        height: "auto",
                        lineHeight: "1.25rem",
                        gap: "0.25rem",
                        fontSize: "0.875rem",
                        fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                        color: "hsl(152, 60%, 32%)",
                        textTransform: "none",
                        transition: "opacity 0.2s ease",
                        "&:hover": {
                          opacity: 0.8,
                          bgcolor: "transparent",
                        },
                        "& .MuiButton-endIcon": {
                          ml: 0,
                        },
                      }}
                    >
                      <FormattedMessage
                        id={showFullDesc ? "company_view.profile.show_less" : "company_view.profile.show_more"}
                      />
                    </Button>
                  )}
                  <Box sx={{ clear: "both" }} />
                </>
              );
            })()}

            {featuredTestimonial && (
              <Box
                component="blockquote"
                sx={{
                  ml: 0,
                  mt: 2,
                  pl: 2,
                  borderLeft: "3px solid hsl(152, 60%, 32%)",
                  position: "relative",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    fontStyle: "italic",
                    color: "hsl(222.2, 84%, 4.9%)",
                    lineHeight: 1.625,
                    fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                  }}
                >
                « {featuredTestimonial.content} »
                </Typography>

                {(featuredTestimonial.author_name || featuredTestimonial.role) && (
                  <Box component="footer" sx={{ mt: 2 }}>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        color: "hsl(222.2, 84%, 4.9%)",
                        lineHeight: 1.3,
                        fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                      }}
                    >
                      {[featuredTestimonial.author_name, featuredTestimonial.role].filter(Boolean).join(", ")}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {galleryImages.length > 0 && (
            <Box>
              <SectionHeader
                title={intl.formatMessage(
                  { id: "company_view.profile.life_at_title" },
                  {
                    companyName:
                      company?.name || intl.formatMessage({ id: "company_view.profile.this_company" }),
                  }
                )}
              />
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "0.5rem", borderRadius: "0.75rem", overflow: "hidden" }}>
                {galleryImages.map((src, index) => (
                  <Box
                    key={`${src}-${index}`}
                    sx={{
                      aspectRatio: "4 / 3",
                      overflow: "hidden",
                      bgcolor: "hsl(210, 40%, 96.1%)",
                      "&:hover img": {
                        transform: "scale(1.05)",
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={src}
                      alt={`Team photo ${index + 1}`}
                      loading="lazy"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        transition: "transform 0.3s ease",
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          <Box>
            {advantages.length > 0 && (
              <>
                <SectionHeader title={intl.formatMessage({ id: "company_view.profile.advantages.title" })} /><Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr" }, gap: "0.75rem" }}>
                  {(showAllAdvantages ? advantages : advantages.slice(0, 6)).map((label) => {
                    const Icon = ADVANTAGE_ICONS[label] || Sparkles;

                    return (
                      <Paper
                        key={label}
                        variant="outlined"
                        sx={{
                          p: "0.875rem",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.75rem",
                          borderRadius: "0.75rem",
                        }}
                      >
                        <Box
                          sx={{
                            width: "2.25rem",
                            height: "2.25rem",
                            borderRadius: "999px",
                            bgcolor: "hsla(152, 60%, 32%, 0.12)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <Icon size={16} color="hsl(152, 60%, 32%)" />
                        </Box>
                        <Typography sx={{ fontSize: "0.875rem", color: "#023331", fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}>
                          {intl.formatMessage(
                            {
                              id: `company_view.profile.advantages.items.${label}`,
                              defaultMessage: formatAdvantage(label),
                            }
                          )}
                        </Typography>
                      </Paper>
                    );
                  })}
                </Box>
              </>
            )}
            {advantages.length > 6 && !showAllAdvantages && (
              <Button
                onClick={() => setShowAllAdvantages(true)}
                disableRipple
                sx={{ mt: 1.25, p: 0, minWidth: 0, textTransform: "none", color: "hsl(152, 60%, 32%)", fontFamily: "Segoe UI Semibold, system-ui, sans-serif", "&:hover": { bgcolor: "transparent" } }}
              >
                <FormattedMessage
                  id="company_view.profile.advantages.show_more"
                  values={{ count: advantages.length - 6 }}
                />
              </Button>
            )}
          </Box>

          {recruitmentSteps.length > 0 && (
            <Box>
              <Typography
                sx={{
                  fontSize: "1.125rem",
                  fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                  color: "hsl(222.2, 84%, 4.9%)",
                  mb: 2.5,
                  borderBottom: "2px solid hsl(152, 60%, 32%)",
                  display: "inline-block",
                  pb: "0.25rem",
                }}
              >
                <FormattedMessage id="company_view.profile.recruitment_process" />
              </Typography>

              <Box
                sx={{
                  borderRadius: "0.75rem",
                  border: "1px solid hsl(214.3, 31.8%, 91.4%)",
                  bgcolor: "hsl(0, 0%, 100%)",
                  p: 3,
                  maxWidth: "48rem",
                }}
              >
                <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
                  {recruitmentSteps.map((step, i) => (
                    <Box
                      component="li"
                      key={`${step.step}-${i}`}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "0.75rem",
                        "&:not(:last-of-type)": {
                          mb: 2,
                        },
                      }}
                    >
                      <CircleDot size={16} color="hsl(152, 60%, 32%)" style={{ marginTop: "0.225rem", flexShrink: 0 }} />
                      <Typography sx={{ fontSize: "0.875rem", color: "hsl(215.4, 16.3%, 46.9%)", lineHeight: 1.625 }}>
                        <Box component="span" sx={{ fontFamily: "Segoe UI Bold, system-ui, sans-serif", color: "hsl(222.2, 84%, 4.9%)" }}>
                          {step.step} :{" "}
                        </Box>
                        {step.description}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          )}

          {company?.awards && (
            <Box>
              <SectionHeader title={intl.formatMessage({ id: "company_view.profile.recognition" })} />
              <HTML html={company.awards} />
            </Box>
          )}

          {subsidiaries.length > 0 && (
            <Box>
              <Typography
                sx={{
                  fontSize: "1.125rem",
                  fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                  color: "hsl(222.2, 84%, 4.9%)",
                  mb: 2.5,
                  borderBottom: "2px solid hsl(152, 60%, 32%)",
                  display: "inline-block",
                  pb: "0.25rem",
                }}
              >
                <FormattedMessage id="company_view.profile.subsidiaries" />
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "1fr 1fr",
                  },
                  gap: "1rem",
                }}
              >
                {subsidiaries.map((subsidiary, index) => (
                  <CompanyCard
                    key={subsidiary.id || subsidiary.name}
                    company={subsidiary}
                    index={index}
                  />
                ))}
              </Box>
            </Box>
          )}

        </Stack>

        <Box
          component="aside"
          ref={sidebarColumnRef}
          sx={{
            maxWidth: "100%",
            flexShrink: 0,
            position: "relative",
            alignSelf: "flex-start",
            width: { xs: "100%", md: "300px" },
            height: typeof sidebarColumnHeight === "number" ? `${sidebarColumnHeight}px` : "auto",
          }}
        >
          <Stack
            ref={sidebarContentRef}
            sx={{
              gap: "1.25rem",
              position: sidebarMode === "fixed" ? "fixed" : sidebarMode === "bottom" ? "absolute" : "static",
              top: sidebarMode === "fixed" ? "5rem" : "auto",
              bottom: sidebarMode === "bottom" ? 0 : "auto",
              left: sidebarMode === "fixed" ? `${sidebarLeft}px` : "auto",
              width: sidebarMode === "fixed" ? `${sidebarWidth}px` : "100%",
            }}
          >
            {socialLinks.length > 0 && (
              <Paper variant="outlined" sx={{ p: "1rem", borderRadius: "0.75rem" }}>
                <Typography
                  sx={{
                    mb: "0.75rem",
                    fontSize: "0.75rem",
                    fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                    color: "hsl(215.4, 16.3%, 46.9%)",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}
                >
                  <FormattedMessage id="company_view.profile.follow_us" />
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "0.75rem" }}>
                  {socialLinks.map(({ key, url, Icon }) => (
                    <Box
                      key={`${key}-${url}`}
                      component="a"
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={intl.formatMessage({
                        id: `company_view.profile.social_links.${key}`,
                        defaultMessage: key,
                      })}
                      sx={{
                        width: "2.25rem",
                        height: "2.25rem",
                        borderRadius: "999px",
                        border: "1px solid hsl(214.3, 31.8%, 91.4%)",
                        color: "hsl(215.4, 16.3%, 46.9%)",
                        bgcolor: "hsl(0, 0%, 100%)",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textDecoration: "none",
                        transition: "all 0.2s ease",
                        "&: hover": {
                          color: "hsl(152, 60%, 32%)",
                          borderColor: "hsl(152, 60%, 32%)",
                        },
                      }}
                    >
                      <Icon size={16} />
                    </Box>
                  ))}
                </Box>
              </Paper>
            )}

            <Paper variant="outlined" sx={{ p: "1rem", borderRadius: "0.75rem" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem", mb: "0.75rem" }}>
                <Box
                  sx={{
                    width: "1.5rem",
                    height: "1.5rem",
                    borderRadius: "999px",
                    bgcolor: "hsla(152, 60%, 32%, 0.15)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Briefcase size={12} color="hsl(152, 60%, 32%)" />
                </Box>
                <Typography sx={{ fontSize: "0.875rem", fontFamily: "Segoe UI Semibold, system-ui, sans-serif", color: "hsl(222.2, 84%, 4.9%)" }}>
                  {intl.formatMessage(
                    { id: "company_view.profile.open_roles_count" },
                    { count: jobOffers.length }
                  )}
                </Typography>
              </Box>

              <Stack sx={{ gap: "0.625rem" }}>
                {jobOffers.slice(0, 4).map((job) => (
                  <SmallJobOfferCard
                    key={job.id}
                    offer={job}
                    onClick={() => {
                      window.location.href = `/${i18nLocale}/job-offers/${job.id}`;
                    }}
                  />
                ))}
              </Stack>

              {jobOffers.length > 4 && (
                <Button
                  onClick={() => onChange(null, "jobs")}
                  disableRipple
                  sx={{
                    mt: "0.75rem",
                    p: 0,
                    minWidth: 0,
                    fontSize: "0.875rem",
                    color: "hsl(152, 60%, 32%)",
                    textTransform: "none",
                    "&:hover": { bgcolor: "transparent" },
                  }}
                >
                  <FormattedMessage
                    id="company_view.profile.see_all_jobs"
                    values={{ count: jobOffers.length }}
                  />{" "}
                  →
                </Button>
              )}
            </Paper>
          </Stack>
        </Box>
      </Box>

      {relatedCompanies.length > 0 && (
        <Box>
          <SectionHeader
            icon={Building2}
            title={intl.formatMessage({ id: "company_view.profile.related_companies" })}
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "1fr 1fr",
                xl: "1fr 1fr 1fr",
              },
              gap: "1rem",
            }}
          >
            {relatedCompanies.map((relatedCompany, index) => (
              <CompanyCard
                key={relatedCompany.id || relatedCompany.name}
                company={relatedCompany}
                index={index}
                onClick={() => {
                  window.location.href = `/${i18nLocale}/companies/${relatedCompany.encoded_name}/${relatedCompany.id}`;
                }}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CompanyProfileTab;
